import { Dispatch } from 'redux';

import { QuotationService } from 'app/services/quotation.service';
import {
  changeFieldValueAndUpdate,
  setQuestionVisibility,
} from 'app/services/businessFunctionHelpers';
import { FieldsEnum, FormsEnum } from 'app/shared/enums';
import { insurantSelectionValues } from 'app/shared/interfaces/CalculationDataByModelPaths.interface';
import {
  ConsentsActions,
  ConsentsState,
  ShouldCheckIfConsentResetRequiredState,
} from 'app/store/data/consents';

type ConsentResetRequestData = {
  email: string | null;
  phoneNumber: string | null;
  mainOwner: ConsentResetPersonData;
  insurant: ConsentResetPersonData;
};

type ConsentResetPersonData = {
  name: string | null;
  lastName: string | null;
  peselNumber: string | null;
};

const defaultConsentResetRequestData: ConsentResetRequestData = {
  email: null,
  phoneNumber: null,
  mainOwner: {
    name: null,
    lastName: null,
    peselNumber: null,
  },
  insurant: {
    name: null,
    lastName: null,
    peselNumber: null,
  },
};

export const buildConsentResetRequestData = (
  data: Partial<ConsentResetRequestData>
): ConsentResetRequestData => ({
  ...defaultConsentResetRequestData,
  ...data,
  mainOwner: {
    ...defaultConsentResetRequestData.mainOwner,
    ...data.mainOwner,
  },
  insurant: {
    ...defaultConsentResetRequestData.insurant,
    ...data.insurant,
  },
});

export type ConsentResetTrackedPerson =
  | 'owner'
  | 'coowner1'
  | 'coowner2'
  | 'different';

export type ConsentResetPersonGroup = 'mainOwner' | 'insurant';
export type ConsentResetPersonField = 'name' | 'lastName' | 'peselNumber';

export const shouldCheckConsentResetForTrackedPerson = (
  trackedPerson: ConsentResetTrackedPerson,
  insurantSelection?: insurantSelectionValues
): boolean => {
  switch (trackedPerson) {
    case 'owner':
      return !insurantSelection || insurantSelection === 'owner';
    case 'coowner1':
      return insurantSelection === 'coowner1';
    case 'coowner2':
      return insurantSelection === 'coowner2';
    case 'different':
      return insurantSelection === 'different';
    default:
      return false;
  }
};

export const getConsentResetPersonGroup = (
  insurantSelection?: insurantSelectionValues
): ConsentResetPersonGroup => (insurantSelection ? 'insurant' : 'mainOwner');

export const getConsentResetPersonFlag = (
  shouldCheckIfConsentResetRequired: ShouldCheckIfConsentResetRequiredState,
  personGroup: ConsentResetPersonGroup,
  field: ConsentResetPersonField
) => shouldCheckIfConsentResetRequired[personGroup][field];

export async function checkAndResetMarketingConsentIfRequired({
  quotationId,
  dispatch,
  shouldCheck,
  requestData,
  fieldToResetPath,
  formName = FormsEnum.CalculationForm,
  consentFieldKey = FieldsEnum.InsuranceMarketingEmailConsent,
}: {
  quotationId: string;
  dispatch: Dispatch;
  shouldCheck: boolean;
  requestData: Partial<ConsentResetRequestData>;
  fieldToResetPath:
    | {
        type: 'root';
        field: 'email' | 'phoneNumber';
      }
    | {
        type: 'person';
        personGroup: ConsentResetPersonGroup;
        field: ConsentResetPersonField;
      };
  formName?: FormsEnum;
  consentFieldKey?: FieldsEnum;
}) {
  if (!shouldCheck) {
    return;
  }

  let shouldDisableAllConsentResetChecks = false;

  try {
    const {
      data: resetRequired,
    } = await QuotationService.checkIfConsentResetRequired({
      quotationId,
      data: buildConsentResetRequestData(requestData),
    });

    if (resetRequired) {
      shouldDisableAllConsentResetChecks = true;
      changeFieldValueAndUpdate(consentFieldKey, null, formName);
      setQuestionVisibility({
        key: consentFieldKey,
        formName,
        visible: true,
      });
    }
  } catch {
  } finally {
    if (shouldDisableAllConsentResetChecks) {
      dispatch(
        ConsentsActions.setShouldCheckIfConsentResetRequired({
          email: false,
          phoneNumber: false,
          mainOwner: {
            name: false,
            lastName: false,
            peselNumber: false,
          },
          insurant: {
            name: false,
            lastName: false,
            peselNumber: false,
          },
        })
      );
      return;
    }

    const nextResetState: Partial<
      ConsentsState['shouldCheckIfConsentResetRequired']
    > =
      fieldToResetPath.type === 'root'
        ? {
            [fieldToResetPath.field]: false,
          }
        : {
            [fieldToResetPath.personGroup]: {
              [fieldToResetPath.field]: false,
            },
          };

    dispatch(
      ConsentsActions.setShouldCheckIfConsentResetRequired(nextResetState)
    );
  }
}
